import type {
  GetContextMenuItemsParams,
  GetQuickFilterTextParams,
  GridApi,
  GridOptions,
  ICellRendererFunc,
  ICellRendererParams,
  IRowNode,
  MenuItemDef,
  ProcessCellForExportParams,
  RowSelectionOptions,
} from 'ag-grid-enterprise';
import { compact, first, isArray, isObject, last } from 'lodash-es';
import { logger } from '../../utils';
import { getExpandCollapseMenus } from '../../utils/gridMenu';
import { getCellDisplayValue } from '../AgGrid/agGridGetCellValue';
import { IconName } from '../Icons/IconNames';
import type { BlotterTableContextProps } from './BlotterTableContext';
import { AGGRID_AUTOCOLUMN_ID } from './types';

/** This list of imports must be updated as we support more locales.
 *  We have a test in BlotterTable.test.tsx to ensure that we don't miss adding new supported locale imports here. */
import {
  AG_GRID_LOCALE_DE,
  AG_GRID_LOCALE_EN,
  AG_GRID_LOCALE_ES,
  AG_GRID_LOCALE_FR,
  AG_GRID_LOCALE_IT,
  AG_GRID_LOCALE_PL,
} from '@ag-grid-community/locale';

/** Given a key, grabs the component stored in AgGrid. This will usually be a reused CellRenderer component. */
export function getAgGridComponent(api: GridApi, key: string): any {
  return api.getGridOption('components')?.[key];
}

/**
 * In some cases, we define the cellRenderer property of ColDefs as a string. This is done when the cell renderer we wish to use is
 * stored for reusability reasons in AgGrid's "component" library. In these cases, you can grab that cell renderer's function by its key
 * using this utility.
 *
 * Pass in an API and the Column ID for the column you want the cell renderer for, and you'll get the cell renderer function returned.
 *
 * If the cell renderer is a function to begin with, then it'll just return that.
 */
export function getCellRendererFunc(api: GridApi, colID: string): ICellRendererFunc | undefined {
  const cellRenderer: ICellRendererFunc | string | undefined = api.getColumn(colID)?.getColDef().cellRenderer;
  if (typeof cellRenderer === 'string') {
    // Hard to get any better type checking here...
    const cellRendererParams: object | undefined = api.getColumn(colID)?.getColDef().cellRendererParams ?? {};
    const cellRendererFunc = getAgGridComponent(api, cellRenderer) as ICellRendererFunc | undefined;
    if (!cellRendererFunc) {
      return undefined;
    }

    // If we got the cellRendererFunc from the aggrid component "library", we need to also grab the cellRendererParams defined on the column
    // and apply those. We're not getting the usual help from AgGrid here where they'd usually do this themselves
    const cellRendererFuncWithParams: ICellRendererFunc = params =>
      cellRendererFunc({ ...params, ...cellRendererParams });

    return cellRendererFuncWithParams;
  }

  return cellRenderer;
}

/**
 * Given a list of nodes, which may be either group or leaf nodes, return all
 * the leaf nodes.
 *
 * @param node The nodes to operate on
 * @returns All leaf nodes under the given nodes
 */
export function getAllLeafNodesAfterFilter(node: IRowNode | IRowNode[]): IRowNode[] {
  if (isArray(node)) {
    return node.flatMap(childNode => getAllLeafNodesAfterFilter(childNode));
  }
  if (!node.group) {
    return [node];
  }
  if (!node.childrenAfterFilter) {
    return [];
  }

  return node.childrenAfterFilter.flatMap(childNode => getAllLeafNodesAfterFilter(childNode));
}

/**
 * Gets all nodes found within the provided group node by traversing the tree within recursively.
 * If the provided groupNode is not a group (groupNode.group === false), returns empty array.
 *
 * Does **not** include the groupNode itself in the returned array.
 */
export function getAllNodesInGroup(groupNode: IRowNode): IRowNode[] {
  if (!groupNode.group) {
    return [];
  }

  const nodes: IRowNode[] = [];
  performActionForChildNodesRecursively(groupNode, node => nodes.push(node));
  return nodes;
}

export function expandAllNodesInGroup(groupNode: IRowNode): void {
  if (!groupNode.group) {
    return;
  }

  performActionForChildNodesRecursively(groupNode, node => node.setExpanded(true));
}

/**
 * Expand parent groups for the given nodes, scroll the first node into view, and flash all nodes.
 *
 * If a model update (e.g. websocket confirmation) replaces the node during the flash window,
 * the flash is re-applied to the fresh node so it isn't silently cleared.
 *
 * @param api - The AgGrid API
 * @param nodes - Row nodes to scroll to and flash
 * @param options.position - Where to position the first node in the viewport
 */
export function expandParentsEnsureVisibleAndFlash(
  api: GridApi,
  nodes: IRowNode[],
  options?: {
    position?: 'top' | 'middle' | 'bottom';
  }
): void {
  const ENSURE_VISIBLE_DELAY_MS = 20;
  const FLASH_DURATION_MS = 1200;
  const FADE_DURATION_MS = 1800;

  if (nodes.length === 0) {
    return;
  }

  nodes.forEach(node => getAllParentsOfNodeInclusive(node).forEach(n => n.setExpanded(true)));

  // Let the DOM settle after parent expansion before scrolling
  setTimeout(() => {
    if (!isGridApiReady(api)) {
      return;
    }
    api.ensureNodeVisible(nodes[0], options?.position ?? 'middle');
    // Allow AG Grid to render newly-visible virtualized rows before flashing
    setTimeout(() => {
      if (!isGridApiReady(api)) {
        return;
      }
      const flashParams = { flashDuration: FLASH_DURATION_MS, fadeDuration: FADE_DURATION_MS };
      api.flashCells({ rowNodes: nodes, ...flashParams });

      // Guard against model updates (e.g. websocket data) replacing the node and clearing the flash CSS.
      // When AG Grid replaces a node internally, it re-creates the DOM cells, stripping any flash classes.
      const nodeIds = compact(nodes.map(n => n.id));
      if (nodeIds.length > 0) {
        let knownRefs: (IRowNode | null | undefined)[] = nodeIds.map(id => nodes.find(n => n.id === id) ?? null);
        const onModelUpdated = () => {
          if (!isGridApiReady(api)) {
            return;
          }
          const freshNodesRaw = nodeIds.map(id => api.getRowNode(id));
          const hasChanged = freshNodesRaw.some((n, i) => n != null && n !== knownRefs[i]);
          if (hasChanged) {
            knownRefs = freshNodesRaw;
            api.flashCells({ rowNodes: compact(freshNodesRaw), ...flashParams });
          }
        };
        api.addEventListener('modelUpdated', onModelUpdated);
        setTimeout(() => {
          if (isGridApiReady(api)) {
            api.removeEventListener('modelUpdated', onModelUpdated);
          }
        }, FLASH_DURATION_MS + FADE_DURATION_MS);
      }
    }, 50);
  }, ENSURE_VISIBLE_DELAY_MS);
}

/**
 * Perform some action on behalf of the passed node and all its possible child nodes as well recursively
 */
function performActionForChildNodesRecursively(node: IRowNode, action: (node: IRowNode) => void) {
  action(node);
  node.childrenAfterFilter?.forEach(node => performActionForChildNodesRecursively(node, action));
}

/**
 * Get the CSV export data for a set of row nodes.
 *
 * @param params Params object from an ag-grid callback
 * @param nodeIds A set of row node ids that should be exported
 * @returns CSV export of the selected nodes, for the currently visible columns
 */
export function agGridGetCSV(params: GetContextMenuItemsParams | ICellRendererParams, nodeIds: Set<string>): string {
  return (
    params.api.getDataAsCsv({
      skipRowGroups: true,
      onlySelected: false,
      suppressQuotes: false,
      columnSeparator: ',',
      // Only include columns that are visible with header names to exclude action button / spacer columns
      columnKeys: params.api.getAllDisplayedColumns()?.filter(col => col.getColDef().headerName !== ''),
      processCellCallback: getParamsFormatted,
      shouldRowBeSkipped(innerParams) {
        if (!innerParams.node?.id) {
          return true;
        }
        return !nodeIds.has(innerParams.node.id);
      },
    }) ?? ''
  );
}

export type ExportGridMode = 'CSV' | 'Excel';

/** Expanded AgGrid Context ({@link BlotterTableContextProps}) type that supports
 * value retrieval for custom grouping levels */
export interface GetValueForGroupNodeContext extends BlotterTableContextProps {
  /** Custom Override for Retrieving Group Node data */
  getValueForGroupedNode: (node: IRowNode, mode: ExportGridMode) => string;
}

function isGetValueForGroupNodeContext(context: BlotterTableContextProps): context is GetValueForGroupNodeContext {
  return (context as GetValueForGroupNodeContext)?.getValueForGroupedNode != null;
}

export type ParamsFormattedOptions = {
  /** Whether to export date as raw data format */
  exportDatesRaw?: boolean;
};

/**
 * Get a formatted value for an ag-grid cell
 * @param params Params object from an ag-grid callback
 * @returns String representation of the cell value
 */
export function getParamsFormatted(
  params: GetContextMenuItemsParams | ProcessCellForExportParams,
  mode: ExportGridMode = 'CSV',
  options?: ParamsFormattedOptions
): string {
  // If we're working with a cell in the grouped column, we want to iterate over all layers and print the grouping structure
  // Printing a result like: "BTC > Future" for example
  const node = params.node;
  const column = params.column;
  if (column && column.getColId() === AGGRID_AUTOCOLUMN_ID && node) {
    // For tree-value grids, use the callback to process how to get the value for the group node
    if (isGetValueForGroupNodeContext(params.context.current)) {
      return params.context.current.getValueForGroupedNode(node, mode);
    }

    const isTreeBlotter = params.api.getGridOption('treeData');
    if (isTreeBlotter) {
      const parents = getAllParentsOfNodeInclusive(node);
      return parents
        .reverse()
        .map(parentNode => {
          // If the Tree node has data on it, then there'll be a valueGetter implemented to resolve the value from data. If not, use the node.key.
          // The node.key in a tree row will be the node's element of the dataPath. This'll usually be an ID.
          // If there is no node.data, then we assume that the implementer's valueFormatter will still properly convert node.key to a display label.
          const value = parentNode.data
            ? params.api.getCellValue({ colKey: column, rowNode: parentNode })
            : parentNode.key;

          return (
            getCellDisplayValue({
              ...params,
              node: parentNode,
              value: value,
            }) ?? 'None'
          );
        })
        .join(' > ');
    }

    // Else regular row-grouped blotter
    const rowGroupColumns = params.api.getRowGroupColumns();
    return (
      rowGroupColumns
        // We need to get the correct value at each layer of grouping, and also pass the column for the layer of grouping
        .map(
          column =>
            getCellDisplayValue({
              ...params,
              value: params.api.getCellValue({ colKey: column, rowNode: node }),
              column: column,
            }) ?? 'None'
        )
        .join(' > ')
    );
  }

  // [DEAL-5135] Ensuring date is exported in a consistent format (UTC) across all timezones.
  if (options?.exportDatesRaw) {
    const cellDataType = params.column?.getColDef().cellDataType;
    if (cellDataType === 'dateString') {
      const rawDate = params.value?.toString();
      if (rawDate) {
        return rawDate;
      }
    }
  }

  const formattedValue = getCellDisplayValue(params);

  // If we got a string, return that
  if (typeof formattedValue === 'string') {
    return formattedValue;
  }

  // For size/price the value is an object containing value and currency.
  // {value: '100', currency: 'USD'}
  // In those cases access the numeric value.
  if (typeof formattedValue?.value === 'string') {
    return formattedValue.value;
  }

  // In some cases our valueGetters return arrays or objects which are then exported as [object object]. This code forces
  // them to be formatted to strings.
  if (isObject(params.value) || isArray(params.value)) {
    try {
      return JSON.stringify(params.value);
    } catch (e) {
      logger.error(e as Error);
    }
  }

  if (params.value == null) {
    return '';
  }

  return params.value.toString();
}

/**
 * Ensure that the csv cell value returned by aggrid is prefixed if the content is a formula
 */
export function cellCsvSafety(cellContent: string): string {
  // https://owasp.org/www-community/attacks/CSV_Injection
  let needsSafetyPrefix = ['=', '@', '+'].some(char => cellContent.startsWith(char));
  if (cellContent.startsWith('-')) {
    // if -, test if the cell content is a number
    if (cellContent.length > 1 && !Number.isNaN(Number(cellContent.slice(1)))) {
      return cellContent;
    }
    needsSafetyPrefix = true;
  }
  return needsSafetyPrefix ? `'${cellContent}` : cellContent;
}

export const alphabeticalGroupOrder =
  /**
   * Our default group order comparator, to ensure groups are sorted alphabetically by default
   * @param params Ag grid group order params
   */
  function alphabeticalGroupOrder({ nodeA, nodeB, api }) {
    // The params passed to this group ordering function don't work out of the box with the getCellDisplayValue expected params
    // so we hook them up correctly here and attempt to do the ordering comparison on the display values
    const nodeADisplayValue =
      getCellDisplayValue({
        node: nodeA,
        column: nodeA.rowGroupColumn,
        value: nodeA.key,
        api,
      }) || '';

    const nodeBDisplayValue =
      getCellDisplayValue({
        node: nodeB,
        column: nodeB.rowGroupColumn,
        value: nodeB.key,
        api,
      }) || '';

    return nodeADisplayValue.localeCompare?.(nodeBDisplayValue);
  } satisfies GridOptions['initialGroupOrderComparator'];

/**
 * Removes repeated and last "separator" entries in a list of string | MenuItemDef
 */
export function removeUnnecessarySeparators(items: (string | MenuItemDef)[]) {
  const result = items.reduce(
    (arr, item, index) => {
      if (item === 'separator' && arr[arr.length - 1] === item) {
        return arr;
      }

      arr.push(item);
      return arr;
    },
    [] as (string | MenuItemDef)[]
  );

  if (last(result) === 'separator') {
    result.splice(result.length - 1, 1);
  }

  if (first(result) === 'separator') {
    result.splice(0, 1);
  }

  return result;
}

/** Defines the AgGrid "basic params properties" we need to make this function work correctly. */
type IsEditingThisCellParams = Pick<ICellRendererParams, 'api' | 'column' | 'node'>;

/**
 * Given some set of basic AgGrid function params, returns whether or not the current node is being edited.
 */
export function isEditingThisCell(params: IsEditingThisCellParams): boolean {
  const cellsBeingEdited = params.api.getEditingCells();

  // Go through all cells that are being edited right now and see if any of these cells are us.
  return cellsBeingEdited != null
    ? cellsBeingEdited.some(
        cell => cell.column.getColId() === params.column?.getColId() && cell.rowIndex === params.node.rowIndex
      )
    : false;
}

/**
 * Given a node, will traverse through the node.parent property upwards until it hits the root element of the blotter
 * The returned array of nodes is inclusive, meaning that the given starting node itself will also be included
 * @param node The starting node
 * @returns a list of nodes, where the first node is the starting node, and then each later index is the next parent upwards.
 * If you want the list to start with the top-level parent of this node, simply do Array.reverse() on the returned list.
 */
export function getAllParentsOfNodeInclusive(node: IRowNode<any>): IRowNode<any>[] {
  const parents: IRowNode<any>[] = [];
  let workingNode = node;

  // In AgGrid, the omnipresent ROOT_NODE has level=-1. Iterate while level is 0 or above.
  while (workingNode.level >= 0) {
    parents.push(workingNode);
    if (workingNode.parent == null) {
      break;
    }
    workingNode = workingNode.parent;
  }

  return parents;
}

/**
 * This function returns whether or not the provided node is expanded, as in is taller / greater in height, than the
 * default row height for the grid.
 */
export function isRowHeightExpanded(node: IRowNode<unknown>, api: GridApi<unknown>): boolean {
  const defaultRowHeight = api.getGridOption('rowHeight');
  if (defaultRowHeight == null || node.rowHeight == null) {
    return false;
  }

  return node.rowHeight > defaultRowHeight;
}

/** Default AgGrid params to do multiselection of rows without checkboxes being present */
export const DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS: RowSelectionOptions = {
  mode: 'multiRow',
  checkboxes: false,
  headerCheckbox: false,
  enableClickSelection: true,
  copySelectedRows: true,
};

export const BLOTTER_SELECTION_MULTI_PARAMS_WITH_CHECKBOXES: RowSelectionOptions = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  enableClickSelection: false,
  copySelectedRows: true,
};

/** Default AgGrid params for single selection */
export const DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS: RowSelectionOptions = {
  mode: 'singleRow',
  checkboxes: false,
  enableClickSelection: true,
  copySelectedRows: true,
};

/** Row Selection arg for readonly or custom row selection grids */
export const DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS: RowSelectionOptions = {
  ...DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  enableClickSelection: false,
};

/** Type Guard that validates that AgGrid api is not destroyed */
export function isGridApiReady<T>(api: GridApi<T> | null | undefined): api is GridApi<T> {
  return api != null && api.isDestroyed() === false;
}

/**
 * This function returns the AgGrid API if it is not destroyed, otherwise it returns undefined.
 *
 * @example
 * ```ts
 * safeGridApi(gridApi)?.removeEventListener('modelUpdated', handleModelUpdated);
 * ```
 *
 * @param api The AgGrid API to check. Allows you to pass undefined / null for simplicity
 * @returns The AgGrid API if it is not destroyed, otherwise undefined
 */
export function safeGridApi<TData = any>(api: GridApi<TData> | null | undefined): GridApi<TData> | undefined {
  if (!isGridApiReady(api)) {
    return undefined;
  }
  return api;
}

/**
 * Checks if all toggleable columns are hidden in the grid.
 *
 * A column is considered "toggleable" if it:
 * - Has a colId of AGGRID_AUTOCOLUMN_ID (the auto-group column)
 * - Does not have `suppressHeaderMenuButton: true` (columns without header menu buttons are excluded)
 * - Does not have `lockVisible: true` (locked columns are excluded)
 */
export function checkAllColumnsHidden(api: GridApi | null | undefined): boolean {
  if (!api) {
    return false;
  }
  const columnState = api.getColumnState();
  if (!columnState) {
    return false;
  }
  const toggleableColumns = columnState.filter(({ colId }) => {
    const colDef = api.getColumnDef(colId);
    if (colDef?.colId === AGGRID_AUTOCOLUMN_ID) {
      // Always include the auto-group column in this filtered list.
      return true;
    }
    // If the column is not suppressed and not locked, it is toggleable
    return !colDef?.suppressHeaderMenuButton && !colDef?.lockVisible;
  });
  return toggleableColumns.every(col => col.hide === true);
}

export function resetColumnsIfAllHidden(params: { api: GridApi | null | undefined }): void {
  if (params.api && checkAllColumnsHidden(params.api)) {
    params.api.resetColumnState();
  }
}

const FILTER_PARSER_REGEX = /(?:"([^"\n]*?)")|(?:([^\s]+))/;
export function quickFilterParser(text: string | unknown): string[] {
  // not sure on exact type in different cases, so being defensive here
  if (typeof text !== 'string') {
    return [];
  }

  /**
   * this regex is complicated but what this regex + mapping does is this:
   *
   * 'test test' -> ['test', 'test']
   * '"test test"' -> ['test test']
   * '"phrase one" "phrase two"' -> ['phrase one', 'phrase two']
   * '"phrase one" word word word' -> ['phrase one', 'word', 'word', 'word']
   */
  return compact(text.split(FILTER_PARSER_REGEX).map((part: string | undefined) => part?.trim()));
}

/**
 * Our own implementation of the default getQuickFilterText. This function is used to go from a node + column (as in: a cell) to some searchable string.
 *
 * See AgGrid's default implementation
 * https://github.com/ag-grid/ag-grid/blob/26149bf63cc8909b163d84fc64df5626f527a2a8/packages/ag-grid-community/src/filter/quickFilterService.ts#L197
 *
 * It uses the column's filterValueGetter, which we don't want to do. We want to be searching on the formatted value, as
 * in, what's visible to the user in the blotter. We also add in the valueGetter result as well (as long as it is of type string)
 *
 */
export function defaultGetQuickFilterText({ value, ...params }: GetQuickFilterTextParams): string {
  // calling api.getCellValue can be expensive so we do some quick thinking here to figure out if we need to call it or not based on some assumptions

  // Firstly, getQuickFilterText is invoked with the filterValue of the column. But if the column doesn't have any filterValueGetter, it'll be the valueGetter's result.
  const valueIsValueGetterReturn = params.column.getColDef().filterValueGetter == null;
  const valueGetterValue = valueIsValueGetterReturn
    ? value
    : params.api.getCellValue({ rowNode: params.node, colKey: params.column });

  // We also want to grab the formatted value, but we only do this if we see that the column has a valueFormatter defined. If not, we already have the value this'd return directly above
  const valueFormatterValue = params.column.getColDef().valueFormatter
    ? params.api.getCellValue({ rowNode: params.node, colKey: params.column, useFormatter: true })
    : undefined;

  // We're searching on _both_ the value and formatted value. This is to give easy support for cases where the user copy-pastes a complete ID
  return `${typeof valueGetterValue === 'string' ? valueGetterValue : ''} ${valueFormatterValue ?? ''}`;
}

/**
 * Given a node, returns the gridApi. Useful for cases where we need to go beyond the standard capabilities in our columns, such as comparators for example,
 * where we want to check things using the api, but have no other way to achieve it.
 *
 * This should only be used as a last resort and after questioning yourself at least 3 times. :)
 */
interface ExtendedRowNode extends IRowNode {
  beans?: { gridApi?: GridApi };
}
export function getGridApiFromNode(node: ExtendedRowNode | undefined): GridApi | undefined {
  try {
    return node?.beans?.gridApi;
  } catch (e) {
    return undefined;
  }
}

/**
 * Returns the Ag Grid locale text for the given locale.
 *
 * @param locale The locale string (e.g., 'de', 'it', or 'fr-FR').
 * @returns The locale text object corresponding to the given locale.
 */
export function getAgGridLocaleText(locale: string) {
  switch (locale.split('-')[0]) {
    case 'de':
      return AG_GRID_LOCALE_DE;
    case 'it':
      return AG_GRID_LOCALE_IT;
    case 'pl':
      return AG_GRID_LOCALE_PL;
    case 'fr':
      return AG_GRID_LOCALE_FR;
    case 'es':
      return AG_GRID_LOCALE_ES;
    default:
      return AG_GRID_LOCALE_EN;
  }
}

/**
 * Detects if expand/collapse menus should be shown (when tree data is enabled or row groups exist) and adds them to the items array.
 * This function checks if tree data is enabled or if row groups are present in the grid and conditionally adds expand/collapse menu items.
 *
 * @param params The GetContextMenuItemsParams from ag-grid
 * @param items The array of menu items to add expand/collapse menus to
 * @returns The items array with expand/collapse menus added if tree data is enabled or row groups exist
 */
export function addExpandCollapseMenusIfGroupedData<T>(
  params: GetContextMenuItemsParams<T>,
  items: Array<string | MenuItemDef>
): Array<string | MenuItemDef> {
  const isTreeData = params.api.getGridOption('treeData') === true;
  const hasRowGroups = params.api.getRowGroupColumns().length > 0;

  if (isTreeData || hasRowGroups) {
    items.push(...getExpandCollapseMenus(params), 'separator');
  }
  return items;
}

/**
 * Creates a "Choose Columns" menu item and inserts it into the provided menu items array
 * (before "Reset Columns" if present, otherwise appended at the end).
 */
export function addColumnChooserToMainMenu(
  menuItems: Array<string | MenuItemDef>,
  api: GridApi,
  ownerDocument: Document
): void {
  const columnChooserItem: MenuItemDef = {
    name: 'Choose Columns',
    icon: `<i class="ag-icon ${IconName.Columns}"/>`,
    action: () => {
      if (!isGridApiReady(api)) {
        return;
      }
      api.showColumnChooser();
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        const isClickWithinPopup = target.closest('.ag-popup-child') != null;

        if (!isClickWithinPopup) {
          api.hideColumnChooser();
          ownerDocument.removeEventListener('mousedown', handleClickOutside);
        }
      };
      ownerDocument.addEventListener('mousedown', handleClickOutside);
    },
  };

  const resetColumnsIndex = menuItems.findIndex(item =>
    typeof item === 'string' ? item === 'resetColumns' : item.name === 'Reset Columns'
  );

  resetColumnsIndex !== -1
    ? menuItems.splice(resetColumnsIndex, 0, columnChooserItem)
    : menuItems.push(columnChooserItem);
}

/** Returns the first node found with node.group = true */
export function getFirstRowGroupNode<T>(api: GridApi<T>): IRowNode<T> | undefined {
  let rowNode: IRowNode<T> | undefined = undefined;
  // Kinda sad that we have to iterate through all nodes. But I think it might be better than throwing and catching an error to get out of the forEach loop.
  api.forEachNode(node => {
    if (!rowNode && node.group) {
      rowNode = node;
    }
  });
  return rowNode;
}
